import React, { useMemo } from 'react';
import { Contract, Subcontractor, AnexoContrato } from '../../../types';
import { KlSheet } from '../modal/KlSheet';

export interface MobileContractAnnexesSheetProps {
    open: boolean;
    onClose: () => void;
    contract: Contract;
    subcontractor?: Subcontractor | null;
    onCreateAnexo: () => void;
    onEditAnexo: (anexo: AnexoContrato) => void;
    canCreateAnexo?: boolean;
}

export const MobileContractAnnexesSheet: React.FC<MobileContractAnnexesSheetProps> = ({
    open,
    onClose,
    contract,
    subcontractor,
    onCreateAnexo,
    onEditAnexo,
    canCreateAnexo = true
}) => {
    const anexos: AnexoContrato[] = contract.anexos || [];

    const totalAnexos = useMemo(() => {
        return anexos
            .filter(a => a.status === 'Activo')
            .reduce((sum, a) => sum + (a.totalAmount || 0), 0);
    }, [anexos]);

    return (
        <KlSheet
            height="fixed"
            fixedHeight={560}
            open={open}
            onClose={onClose}
            title="Anexos de contrato"
            footer={
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full h-11 rounded-[12px] font-bold text-sm border border-[#BFDBFE] dark:border-blue-900/60 bg-[#EFF6FF] dark:bg-blue-950/60 text-[#1D4ED8] dark:text-blue-300 active:scale-[0.98] transition-transform"
                >
                    Cerrar
                </button>
            }
        >
            <div className="flex flex-col gap-2.5 -mx-1 py-1">
                {/* Subtítulo con nombre del contratista */}
                <div className="text-[12px] text-[#64748B] dark:text-slate-400 font-medium px-1 truncate">
                    {subcontractor?.razonSocial || 'Contratista'}
                </div>

                {/* TARJETA 1: RESUMEN FINANCIERO Y BOTÓN NUEVO ANEXO (Solo montos existentes hoy) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2.5">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-slate-400">
                                Contrato base
                            </div>
                            <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100 font-mono mt-0.5 truncate">
                                ${(contract.totalAmount || 0).toLocaleString('es-CL')}
                            </div>
                        </div>

                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-slate-400">
                                Total Anexos
                            </div>
                            <div className="text-[13px] font-bold text-[#047857] dark:text-emerald-400 font-mono mt-0.5 truncate">
                                +${totalAnexos.toLocaleString('es-CL')}
                            </div>
                        </div>
                    </div>

                    {canCreateAnexo && (
                        <button
                            type="button"
                            onClick={onCreateAnexo}
                            className="w-full h-10 rounded-[12px] font-bold text-[13px] bg-[#2563EB] hover:bg-blue-600 text-white flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform shadow-xs"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14" />
                                <path d="M12 5v14" />
                            </svg>
                            <span>Nuevo anexo</span>
                        </button>
                    )}
                </div>

                {/* LISTA DE TARJETAS DE ANEXOS (13-anexos-de-contrato.html) */}
                {anexos.length === 0 ? (
                    <div className="text-center text-[#64748B] dark:text-slate-400 text-[13px] italic py-8">
                        No hay anexos registrados para este contrato.
                    </div>
                ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {anexos.map((anexo) => (
                            <div
                                key={anexo.id}
                                onClick={() => onEditAnexo(anexo)}
                                className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-1.5 cursor-pointer active:bg-blue-50/40 transition-colors"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <b className="text-[14px] text-[#0F172A] dark:text-slate-100">
                                            Anexo Nº{anexo.numero}
                                        </b>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-[8px] uppercase ${
                                            anexo.status === 'Activo'
                                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                                : anexo.status === 'Anulado'
                                                    ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                                                    : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                                        }`}>
                                            {anexo.status || 'Borrador'}
                                        </span>
                                    </div>
                                    <span className="text-[12px] text-[#64748B] dark:text-slate-400 font-medium">
                                        {anexo.fecha || 'Sin fecha'}
                                    </span>
                                </div>

                                <div className="text-[12px] text-[#64748B] dark:text-slate-400 truncate">
                                    {anexo.description || 'Sin descripción'}
                                </div>

                                <div className="flex justify-between items-baseline pt-1 border-t border-slate-100 dark:border-slate-800">
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-slate-400">
                                        Monto adicional
                                    </span>
                                    <b className="text-[14px] font-mono text-[#047857] dark:text-emerald-400">
                                        + ${(anexo.totalAmount || 0).toLocaleString('es-CL')}
                                    </b>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </KlSheet>
    );
};
